import { useTranslation } from "react-i18next";

import { SignIn } from "@/components/auth/SignIn";

import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { LightAFire } from "./LightAFire";

import { DEMO_ROOM_CODE, type EntryRefusal } from "@/engine/state/roomAccess";

import { extractRoomCode } from "@/engine/state/roomIdentity";
import { REFUSAL_COPY } from "./refusalCopy";
import { JoinIdleScene } from "./JoinIdleScene";
import { JoinFromPhone } from "./JoinFromPhone";
import { prefetchRoom } from "@/adapters/supabase/roomPrefetch";

// The brandbook's filled button, at the height of a way in. Not a gradient:
// the action colour is amber, and it is the one amber object on the screen.
const WAY_IN = "w-full min-h-14 text-sm sm:text-base font-semibold rounded-2xl";

/**
 * Only on a phone, and at whichever door the visitor is standing at.
 *
 * The whole idea of the product is inaudible without headphones, and a phone is
 * the one place the visitor is likely to already have a pair in or to be a
 * second from them. On a desktop the same line is a remark about hardware
 * nobody is holding.
 */
function HeadphoneHint() {
  const { t } = useTranslation();
  return (
    <p
      data-testid="entry-headphones"
      className="sm:hidden text-center text-xs text-muted-foreground"
    >
      {t("entry.headphones")}
    </p>
  );
}

export function CreateTab({
  accountKnown,
  signedIn,
  isConnecting,
  onCreate,
}: {
  accountKnown: boolean;
  signedIn: boolean;
  isConnecting: boolean | undefined;
  onCreate: (name: string) => void;
}) {
  const { t } = useTranslation();
  const busy = Boolean(isConnecting) || !accountKnown;

  // With an account behind it, the door is the page that opens a room.
  if (accountKnown && signedIn)
    return (
      <LightAFire
        busy={busy}
        creating={Boolean(isConnecting)}
        onCreate={onCreate}
      />
    );

  return (
    <div className="mx-auto w-full max-w-md space-y-3 sm:space-y-4">
      {accountKnown ? (
        <SignIn
          intent={{ kind: "create" }}
          reason={t("entry.refusedNeedsAccount")}
        />
      ) : (
        <Button
          data-testid="entry-create"
          variant="default"
          size="xl"
          disabled
          className={WAY_IN}
        >
          <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
          {t("entry.checkingAccount")}
        </Button>
      )}
      <p
        data-testid="entry-create-hint"
        className="text-center text-xs text-muted-foreground"
      >
        {t("entry.createHint")}
      </p>
      <HeadphoneHint />
    </div>
  );
}

/** Join tab: the room-code field, its prefetch-on-type, and the Connect button. */
export function JoinTab({
  roomCode,
  setRoomCode,
  joinsDemoRoom,
  isConnecting,
  entryError,
  onJoin,
  onHandedOver,
}: {
  roomCode: string;
  setRoomCode: (code: string) => void;
  joinsDemoRoom: boolean;
  isConnecting: boolean | undefined;
  entryError: EntryRefusal | null;
  onJoin: () => void;
  /** Where a room a phone hands over goes; absent, the offer is not drawn. */
  onHandedOver?: (code: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 sm:space-y-5">
      <div>
        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3 font-semibold">
          {t("entry.roomCode")}
        </label>
        <Input
          data-testid="entry-room-code"
          type="text"
          value={roomCode}
          onChange={(e) => {
            const next = e.target.value.toLowerCase();
            setRoomCode(next);
            // Asks about the room while the code is still being typed, so the
            // press has nothing left to wait for.
            prefetchRoom(extractRoomCode(next));
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData("text");
            if (!pasted) return;
            e.preventDefault();
            setRoomCode(extractRoomCode(pasted));
            prefetchRoom(extractRoomCode(pasted));
          }}
          onBlur={() => setRoomCode(extractRoomCode(roomCode))}
          onKeyDown={(e) => e.key === "Enter" && !isConnecting && onJoin()}
          placeholder={DEMO_ROOM_CODE}
          disabled={isConnecting}
          className="h-11 sm:h-12 lg:h-14 w-full text-center text-base sm:text-lg font-mono tracking-widest rounded-2xl bg-muted/30 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
        />
        {entryError && (
          <p
            data-testid="entry-room-error"
            role="alert"
            className="mt-2 text-center text-xs sm:text-sm text-destructive"
          >
            {t(REFUSAL_COPY[entryError])}
          </p>
        )}
        {/* A removal is not a code that was typed wrong, so the hint about
            mixing up I and 1 is not offered beside it. */}
        {entryError && entryError !== "removed-by-host" && (
          <p
            data-testid="entry-code-hint"
            className="mt-1 text-center text-xs text-muted-foreground"
          >
            {t("entry.codeHint")}
          </p>
        )}
      </div>
      <Button
        data-testid="entry-join"
        // One orange call on home (#802): the demo is an offer beside Create
        // Room, not a second call; a typed code is the visitor's own choice.
        variant={joinsDemoRoom ? "outline" : "default"}
        size="xl"
        onClick={onJoin}
        disabled={isConnecting}
        className={WAY_IN}
      >
        {isConnecting ? (
          <>
            <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
            {t("entry.connecting")}
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 mr-2" />
            {joinsDemoRoom ? t("entry.tryDemoRoom") : t("entry.connect")}
          </>
        )}
      </Button>
      {/* The invitation is often on a phone: the second way in, beside the
          first, rather than a line somebody has to find under the card. */}
      {onHandedOver && (
        <JoinFromPhone onJoin={onHandedOver} disabled={isConnecting} />
      )}
      {/* What the microphone will do is said at the room's threshold, where
          the visitor decides how to arrive. */}
      <HeadphoneHint />
      {/* Nothing typed yet: the way in shows a fire rather than ending in an
          empty block beside the character card (#834). */}
      {!roomCode && <JoinIdleScene />}
    </div>
  );
}
